/**
 * PremiumModal.jsx
 * Premium Modal System
 *
 * Design System Premium Fleet v2.0
 * Consistent, accessible modals with animations
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  alpha,
  keyframes,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// ANIMATIONS
// ============================================================================

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;

// ============================================================================
// SIZE CONFIG
// ============================================================================

const sizes = {
  xs: 320,
  sm: 400,
  md: 560,
  lg: 720,
  xl: 960,
  full: '100vw',
};

// ============================================================================
// MODAL CONTEXT
// ============================================================================

const ModalContext = createContext(null);

// ============================================================================
// MODAL HEADER
// ============================================================================

export const ModalHeader = ({
  title,
  subtitle,
  icon,
  onClose,
  showCloseButton = true,
  children,
}) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 2.5,
        borderBottom: `1px solid ${tokens?.border?.subtle || '#374151'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: tokens?.radius?.md || 8,
              bgcolor: alpha(tokens?.brand?.primary || '#6366f1', 0.1),
              color: tokens?.brand?.primary || '#6366f1',
              flexShrink: 0,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && (
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: tokens?.text?.primary || '#f9fafb',
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 14,
                color: tokens?.text?.secondary || '#9ca3af',
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </Box>
      </Box>

      {showCloseButton && onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: tokens?.text?.tertiary || '#6b7280',
            '&:hover': {
              color: tokens?.text?.secondary || '#9ca3af',
              bgcolor: alpha(tokens?.text?.secondary || '#9ca3af', 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};

// ============================================================================
// MODAL BODY
// ============================================================================

export const ModalBody = ({ children, noPadding = false, sx = {} }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        flex: 1,
        px: noPadding ? 0 : 3,
        py: noPadding ? 0 : 3,
        overflowY: 'auto',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// MODAL FOOTER
// ============================================================================

export const ModalFooter = ({
  children,
  // Preset props
  preset,
  onCancel,
  onConfirm,
  cancelLabel,
  confirmLabel,
  confirmVariant = 'primary', // primary, danger
  confirmLoading = false,
  confirmDisabled = false,
  cancelDisabled = false,
  align = 'right', // left, center, right, space-between
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const alignStyles = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
    'space-between': 'space-between',
  };

  // Preset configurations
  const presetConfigs = {
    confirm: {
      cancelLabel: t('modalCancel'),
      confirmLabel: t('modalConfirm'),
    },
    save: {
      cancelLabel: t('modalCancel'),
      confirmLabel: t('modalSave'),
    },
    delete: {
      cancelLabel: t('modalCancel'),
      confirmLabel: t('modalDelete'),
      confirmVariant: 'danger',
    },
    close: {
      confirmLabel: t('modalClose'),
      showCancel: false,
    },
  };

  const config = preset ? presetConfigs[preset] : {};
  const finalCancelLabel = cancelLabel || config.cancelLabel || t('modalCancel');
  const finalConfirmLabel = confirmLabel || config.confirmLabel || t('modalConfirm');
  const finalConfirmVariant = config.confirmVariant || confirmVariant;
  const showCancel = config.showCancel !== false;

  const confirmColors = {
    primary: {
      bg: tokens?.brand?.primary || '#6366f1',
      hover: tokens?.brand?.primaryHover || '#4f46e5',
    },
    danger: {
      bg: tokens?.semantic?.error || '#ef4444',
      hover: '#dc2626',
    },
  };

  const colors = confirmColors[finalConfirmVariant] || confirmColors.primary;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: alignStyles[align] || 'flex-end',
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens?.border?.subtle || '#374151'}`,
      }}
    >
      {children || (
        <>
          {showCancel && onCancel && (
            <Button
              onClick={onCancel}
              disabled={cancelDisabled}
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 14,
                fontWeight: 500,
                color: tokens?.text?.secondary || '#9ca3af',
                bgcolor: 'transparent',
                borderRadius: tokens?.radius?.md || 8,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha(tokens?.text?.secondary || '#9ca3af', 0.1),
                },
              }}
            >
              {finalCancelLabel}
            </Button>
          )}
          {onConfirm && (
            <Button
              onClick={onConfirm}
              disabled={confirmDisabled || confirmLoading}
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                bgcolor: colors.bg,
                borderRadius: tokens?.radius?.md || 8,
                textTransform: 'none',
                minWidth: 100,
                '&:hover': {
                  bgcolor: colors.hover,
                },
                '&:disabled': {
                  bgcolor: alpha(colors.bg, 0.5),
                  color: alpha('#fff', 0.7),
                },
              }}
            >
              {confirmLoading ? (
                <CircularProgress size={18} sx={{ color: 'inherit' }} />
              ) : (
                finalConfirmLabel
              )}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

// ============================================================================
// PREMIUM MODAL (Base)
// ============================================================================

export const PremiumModal = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
  children,
  maxHeight = '90vh',
  fullScreen = false,
  disableAutoFocus = true,
  disableEnforceFocus = true,
  disableRestoreFocus = true,
}) => {
  const tokens = usePremiumTokens();
  const [isExiting, setIsExiting] = useState(false);
  const modalRef = useRef(null);

  // Handle escape key
  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, closeOnEscape]);

  // Blur active element when modal opens (accessibility fix)
  useEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [open]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsExiting(false);
      onClose?.();
    }, 150);
  }, [onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      handleClose();
    }
  }, [closeOnBackdrop, handleClose]);

  const modalWidth = fullScreen ? '100vw' : (typeof sizes[size] === 'number' ? sizes[size] : sizes[size]);

  if (!open && !isExiting) return null;

  return createPortal(
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: fullScreen ? 'stretch' : 'center',
        justifyContent: 'center',
        p: fullScreen ? 0 : 2,
        bgcolor: alpha('#000', 0.6),
        backdropFilter: 'blur(4px)',
        animation: isExiting
          ? `${keyframes`from { opacity: 1 } to { opacity: 0 }`} 150ms ease-out forwards`
          : `${keyframes`from { opacity: 0 } to { opacity: 1 }`} 200ms ease-out`,
      }}
    >
      <Box
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: modalWidth,
          maxHeight: fullScreen ? '100vh' : maxHeight,
          bgcolor: tokens?.surface?.elevated || '#1f2937',
          borderRadius: fullScreen ? 0 : (tokens?.radius?.xl || 16),
          border: fullScreen ? 'none' : `1px solid ${tokens?.border?.subtle || '#374151'}`,
          boxShadow: tokens?.shadows?.xl || '0 25px 50px -12px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: isExiting
            ? `${scaleOut} 150ms ease-in forwards`
            : `${scaleIn} 200ms ease-out`,
        }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <ModalHeader
            title={title}
            subtitle={subtitle}
            icon={icon}
            onClose={handleClose}
            showCloseButton={showCloseButton}
          />
        )}

        {/* Body */}
        <ModalBody>{children}</ModalBody>

        {/* Footer */}
        {footer}
      </Box>
    </Box>,
    document.body
  );
};

// ============================================================================
// CONFIRM MODAL
// ============================================================================

export const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'primary',
  confirmLoading = false,
  icon,
  size = 'sm',
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const handleConfirm = async () => {
    await onConfirm?.();
    onClose?.();
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      size={size}
      showCloseButton={false}
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleConfirm}
          cancelLabel={cancelLabel || t('modalCancel')}
          confirmLabel={confirmLabel || t('modalConfirm')}
          confirmVariant={confirmVariant}
          confirmLoading={confirmLoading}
        />
      }
    >
      <Box sx={{ textAlign: 'center', py: 2 }}>
        {icon && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha(
                confirmVariant === 'danger'
                  ? tokens?.semantic?.error || '#ef4444'
                  : tokens?.semantic?.warning || '#f59e0b',
                0.1
              ),
              color: confirmVariant === 'danger'
                ? tokens?.semantic?.error || '#ef4444'
                : tokens?.semantic?.warning || '#f59e0b',
              mb: 2,
              '& svg': { fontSize: 28 },
            }}
          >
            {icon}
          </Box>
        )}

        {title && (
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: tokens?.text?.primary || '#f9fafb',
              mb: 1,
            }}
          >
            {title}
          </Typography>
        )}

        {message && (
          <Typography
            sx={{
              fontSize: 14,
              color: tokens?.text?.secondary || '#9ca3af',
              lineHeight: 1.5,
              maxWidth: 320,
              mx: 'auto',
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </PremiumModal>
  );
};

// ============================================================================
// ALERT MODAL
// ============================================================================

export const AlertModal = ({
  open,
  onClose,
  title,
  message,
  type = 'info', // success, error, warning, info
  buttonLabel,
  size = 'xs',
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const typeConfig = {
    success: {
      icon: <CheckCircleIcon />,
      color: tokens?.semantic?.success || '#22c55e',
    },
    error: {
      icon: <ErrorIcon />,
      color: tokens?.semantic?.error || '#ef4444',
    },
    warning: {
      icon: <WarningIcon />,
      color: tokens?.semantic?.warning || '#f59e0b',
    },
    info: {
      icon: <InfoIcon />,
      color: tokens?.semantic?.info || '#3b82f6',
    },
  };

  const config = typeConfig[type] || typeConfig.info;

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      size={size}
      showCloseButton={false}
      footer={
        <ModalFooter align="center">
          <Button
            onClick={onClose}
            sx={{
              px: 4,
              py: 1,
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              bgcolor: config.color,
              borderRadius: tokens?.radius?.md || 8,
              textTransform: 'none',
              '&:hover': {
                bgcolor: alpha(config.color, 0.9),
              },
            }}
          >
            {buttonLabel || t('modalClose')}
          </Button>
        </ModalFooter>
      }
    >
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: alpha(config.color, 0.1),
            color: config.color,
            mb: 2,
            '& svg': { fontSize: 28 },
          }}
        >
          {config.icon}
        </Box>

        {title && (
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: tokens?.text?.primary || '#f9fafb',
              mb: 1,
            }}
          >
            {title}
          </Typography>
        )}

        {message && (
          <Typography
            sx={{
              fontSize: 14,
              color: tokens?.text?.secondary || '#9ca3af',
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </PremiumModal>
  );
};

// ============================================================================
// MODAL PROVIDER
// ============================================================================

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);
  const modalIdRef = useRef(0);

  const showModal = useCallback((modalConfig) => {
    return new Promise((resolve) => {
      const id = ++modalIdRef.current;

      const handleClose = (result = false) => {
        setModals((prev) => prev.filter((m) => m.id !== id));
        resolve(result);
      };

      const modal = {
        id,
        ...modalConfig,
        onClose: () => handleClose(false),
        onConfirm: async () => {
          if (modalConfig.onConfirm) {
            await modalConfig.onConfirm();
          }
          handleClose(true);
        },
      };

      setModals((prev) => [...prev, modal]);
    });
  }, []);

  const confirm = useCallback((config) => {
    return showModal({
      type: 'confirm',
      ...config,
    });
  }, [showModal]);

  const alert = useCallback((config) => {
    return showModal({
      type: 'alert',
      ...config,
    });
  }, [showModal]);

  const closeAll = useCallback(() => {
    setModals([]);
  }, []);

  const contextValue = useMemo(() => ({
    confirm,
    alert,
    closeAll,
  }), [confirm, alert, closeAll]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}

      {/* Render modals */}
      {modals.map((modal) => {
        if (modal.type === 'confirm') {
          return (
            <ConfirmModal
              key={modal.id}
              open={true}
              onClose={modal.onClose}
              onConfirm={modal.onConfirm}
              title={modal.title}
              message={modal.message}
              confirmLabel={modal.confirmLabel}
              cancelLabel={modal.cancelLabel}
              confirmVariant={modal.confirmVariant}
              icon={modal.icon || <WarningIcon />}
            />
          );
        }

        if (modal.type === 'alert') {
          return (
            <AlertModal
              key={modal.id}
              open={true}
              onClose={modal.onClose}
              title={modal.title}
              message={modal.message}
              type={modal.alertType || 'info'}
              buttonLabel={modal.buttonLabel}
            />
          );
        }

        return null;
      })}
    </ModalContext.Provider>
  );
};

// ============================================================================
// USE MODAL HOOK
// ============================================================================

export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    console.warn('useModal must be used within a ModalProvider');
    return {
      confirm: async () => false,
      alert: async () => {},
      closeAll: () => {},
    };
  }

  return context;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default PremiumModal;
